import { Injectable, Inject, PLATFORM_ID, signal, Signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { io, Socket } from 'socket.io-client';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private socket!: Socket;
  private readonly socketUrl = environment.socketUrl;

  // Using Signals for state tracking
  private _isConnected = signal<boolean>(false);
  public isConnected = this._isConnected.asReadonly();

  private currentUserId: number | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  connect(): void {
    if (this.socket?.connected || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const token =
      localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      console.warn('Socket connection skipped: No token found');
      return;
    }

    // Initialize the socket connection with auth token
    this.socket = io(this.socketUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'], // Prefer websocket
      auth: {
        token: token,
      },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    this.setupInternalListeners();

    console.log('Socket initialized manually via connect()');
  }

  private setupInternalListeners(): void {
    this.socket.on('connect', () => {
      console.info('Socket connected:', this.socket.id);
      this._isConnected.set(true);

      // Auto-re-register if we have a userId
      if (this.currentUserId) {
        this.register(this.currentUserId);
      }
    });

    this.socket.on('disconnect', reason => {
      console.warn('Socket disconnected:', reason);
      this._isConnected.set(false);
    });

    this.socket.on('connect_error', error => {
      console.error('Socket connection error:', error);
      this._isConnected.set(false);
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this._isConnected.set(false);
    }
  }

  // Listen to events
  on(event: string): Observable<any> {
    if (!this.socket || !isPlatformBrowser(this.platformId)) {
      console.warn(`Attempted to listen to ${event} without active socket`);
      return new Observable();
    }

    return new Observable(observer => {
      this.socket.on(event, (data: any) => {
        console.log(`Received ${event} with data:`, data);

        observer.next(data);
      });

      // Cleanup listener on unsubscribe
      return () => this.socket?.off(event);
    });
  }

  // Emit events
  emit(event: string, data: any): void {
    if (this.socket && isPlatformBrowser(this.platformId)) {
      this.socket.emit(event, data);
    } else {
      console.warn(`Attempted to emit ${event} without active socket`);
    }
  }

  joinConversation(conversation_id: number): void {
    this.emit('join', conversation_id);
  }

  register(user_id: number): void {
    this.currentUserId = user_id;
    this.emit('register', { userId: user_id });
  }
}
